import {
  findNode,
  getAboveNode,
  getEditorString,
  getNodeLeaf,
  getNodeProps,
  getPluginType,
  InsertNodesOptions,
  isExpanded,
  isType,
  PlateEditor,
  removeNodes,
  setNodes,
  UnwrapNodesOptions,
  Value,
  WrapNodesOptions,
} from '@udecode/plate-common';

import {
  ELEMENT_ANCHOR_LINK,
  ELEMENT_INTERLINK,
  ELEMENT_LINK,
  MyAnchorLinkElement,
} from '../../../plateTypes';
import { CreateAnchorLinkNodeOptions } from '../utils/createAnchorLinkNode';
import { insertAnchorLink } from './insertAnchorLink';
import { unwrapAnchorLink } from './unwrapAnchorLink';
import { upsertLinkText } from './upsertLinkText';
import { wrapAnchorLink } from './wrapAnchorLink';

export type UpsertElementLinkOptions<V extends Value = Value> =
  CreateAnchorLinkNodeOptions & {
    insertNodesOptions?: InsertNodesOptions<V>;
    unwrapNodesOptions?: UnwrapNodesOptions<V>;
    wrapNodesOptions?: WrapNodesOptions<V>;
  };

/**
 * If selection in a link:
 *
 * - Insert text with link, exit If selection is expanded or `update` in a link:
 * - Remove link node, get link text Then:
 * - Insert link node
 */
export const upsertAnchorLink = <V extends Value>(
  editor: PlateEditor<V>,
  { linkId, text, insertNodesOptions }: UpsertElementLinkOptions<V>,
): boolean | void => {
  const at = editor.selection;
  if (!at) return;

  const linkAbove = getAboveNode<MyAnchorLinkElement>(editor, {
    at,
    match: (node) => {
      return isType(editor, node, [
        ELEMENT_LINK,
        ELEMENT_ANCHOR_LINK,
        ELEMENT_INTERLINK,
      ]);
    },
  });

  // Change type and properties of the link
  if (linkAbove) {
    if (
      linkId !== linkAbove[0]?.linkId ||
      linkAbove[0].type !== ELEMENT_ANCHOR_LINK
    ) {
      setNodes<MyAnchorLinkElement>(
        editor,
        { type: ELEMENT_ANCHOR_LINK, linkId },
        { at: linkAbove[1] },
      );
    }

    upsertLinkText(editor, { text });
    return true;
  }

  // selection contains at one edge edge or between the edges
  const linkEntry = findNode<MyAnchorLinkElement>(editor, {
    at,
    match: { type: getPluginType(editor, ELEMENT_ANCHOR_LINK) },
  });

  const [linkNode, linkPath] = linkEntry ?? [];

  let shouldReplaceText = false;
  if (linkPath && text?.length) {
    const linkText = getEditorString(editor, linkPath);
    if (text !== linkText) {
      shouldReplaceText = true;
    }
  }

  if (isExpanded(at)) {
    // anchor and focus in link
    if (linkAbove) {
      unwrapAnchorLink(editor, {
        at: linkAbove[1],
      });
    } else {
      unwrapAnchorLink(editor, {
        split: true,
      });
    }

    wrapAnchorLink(editor, {
      linkId,
    });
    upsertLinkText(editor, { text });

    return true;
  }

  if (shouldReplaceText) {
    removeNodes(editor, {
      at: linkPath,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = getNodeProps(linkNode ?? ({} as any));

  const path = editor.selection?.focus.path;
  if (!path) return;

  // link text should have the focused leaf marks
  const leaf = getNodeLeaf(editor, path);

  insertAnchorLink(
    editor,
    {
      ...props,
      linkId,
      children: [
        {
          ...leaf,
          text,
        },
      ],
    },
    insertNodesOptions,
  );
  return true;
};
